/**
 * How the console reaches a worker's per-session surfaces, now that every
 * `/browsers/*` route on the worker demands a per-launch capability token.
 *
 * The viewer and the thumbnail take deliberately different routes:
 *
 *   - The viewer frames a worker page a human loads, so the token can ride in
 *     the URL once. The worker moves it into a path-scoped HttpOnly cookie and
 *     redirects to the clean address, and every relative request the shell
 *     makes after that — the inspector, the VNC stream, the info poll —
 *     inherits it.
 *   - A thumbnail is an `<img>`. It can carry no header, and a token in its
 *     `src` would never be absorbed into a cookie, so the console would be
 *     holding a twelve-hour capability for every live browser in its URLs,
 *     its history and the load balancer's access log. That one goes through
 *     cluster-manager instead, which already holds the worker key server-side.
 */

/**
 * The worker names the parameter (`ct`, deliberately not the view ticket's
 * `t`); we only fall back if an older worker sends a token without saying so.
 */
const DEFAULT_CONTROL_PARAM = "ct";

/**
 * Attach a per-launch capability to a worker URL a human is about to load.
 *
 * A worker that predates the capability work sends no token, and its routes
 * still answer without one — so no token means the URL is returned untouched
 * rather than broken.
 */
export function withControlToken(
	url: string,
	token: string | undefined | null,
	param?: string | null,
): string {
	if (!token) return url;
	const name = param || DEFAULT_CONTROL_PARAM;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}${name}=${encodeURIComponent(token)}`;
}

/**
 * A live still of one session, served by cluster-manager rather than pulled
 * straight off the worker.
 *
 * `ts` rather than `t` for the cache buster: `t` is the worker's view-ticket
 * parameter, and one name that means "ignore your cache" on one surface and
 * "here is a capability" on another is how the two get confused at the point
 * of use.
 */
export function sessionThumbnailUrl(
	hostname: string,
	launchId: string,
	stamp: number,
): string {
	const host = encodeURIComponent(hostname);
	const launch = encodeURIComponent(launchId);
	return `/api/workers/${host}/browsers/${launch}/screenshot?ts=${stamp}`;
}
